/**
 * Utility functions for the React application
 */

import { DateTime } from 'luxon';

/**
 * Text truncate function
 */
export function textTruncate(str: string, length: number = 10, ending: string = '...'): string {
    if (str.length > length) {
        return str.substring(0, length - ending.length) + ending;
    }
    return str;
}

/**
 * Function to convert hex to rgba
 */
export function hexToRgbA(hex: string, alpha: number = 1): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) {
        return `rgba(0, 0, 0, ${alpha})`;
    }
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Function to calculate percentage based on actual/max
 */
export function percent(max: number, value: number): number {
    if (max === 0) return 0;
    return Math.round((Number(value?.toFixed(2)) / max) * 100);
}

/**
 * Function to download a blob
 */
export function downloadBlob(content: BlobPart, fileName: string, contentType: string): void {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Function to generate an array of numbers between start and end
 */
export function generateNumbers(start: number, end: number): number[] {
    return Array.from({ length: end - start + 1 }, (_, k) => k + start);
}

/**
 * Compare if two strings are equal (case insensitive)
 */
export function equalsIgnoreCase(text: string | null | undefined, other: string | null | undefined): boolean {
    if (!text) return !other;
    if (!other) return !text;
    return text.localeCompare(other, undefined, { sensitivity: 'base' }) === 0;
}

/**
 * Get value of object key doing case insensitive search
 */
export function getByKeyCaseInsensitive<T>(object: Record<string, T>, key: string): T | undefined {
    const foundKey = Object.keys(object).find((oKey) => equalsIgnoreCase(oKey, key));
    return foundKey ? object[foundKey] : undefined;
}

/**
 * Get value of object using a deep search path
 */
export function deepGet(object: any, paths: string[]): any {
    return paths.reduce((xs, x) => (xs && xs[x] ? xs[x] : null), object);
}

/**
 * Convert size in bytes to corresponding upper size
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
    if (!bytes) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Format date and time for display
 */
export function formatDateTime(date: Date, locale: string = 'en-US'): string {
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }).format(date);
}

/**
 * Format date only
 */
export function formatDate(date: Date, locale: string = 'en-US'): string {
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(date);
}

/**
 * Format time only
 */
export function formatTime(date: Date, locale: string = 'en-US'): string {
    return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }).format(date);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number,
    immediate?: boolean
): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null;

    return function executedFunction(...args: Parameters<T>) {
        const later = () => {
            timeout = null;
            if (!immediate) func(...args);
        };

        const callNow = immediate && !timeout;

        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(later, wait);

        if (callNow) func(...args);
    };
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: any): boolean {
    if (value == null) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
}

/**
 * Deep clone an object
 */
export function deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime()) as any;
    if (obj instanceof Array) return obj.map((item) => deepClone(item)) as any;
    if (typeof obj === 'object') {
        const cloned: any = {};
        Object.keys(obj).forEach((key) => {
            cloned[key] = deepClone((obj as any)[key]);
        });
        return cloned;
    }
    return obj;
}

/**
 * Generate a random ID
 */
export function generateId(length: number = 8): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Validate URL format
 */
export function isValidUrl(url: string): boolean {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
    return formatBytes(bytes);
}

/**
 * Capitalize first letter
 */
export function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Convert camelCase to kebab-case
 */
export function camelToKebab(str: string): string {
    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Convert kebab-case to camelCase
 */
export function kebabToCamel(str: string): string {
    return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
}

export function getDateRange(days: number): { start: string | null; end: string | null } {
    const now = DateTime.now();
    const zonedNow = now.setZone('UTC').plus({ minutes: now.offset });
    const start = zonedNow.minus({ days }).startOf('day').toISO();
    const end = zonedNow.toISO();

    return { start, end };
}

/**
 * Get today's date range
 */
export function getTodayDateRange(): { start: string | null; end: string | null } {
    const now = DateTime.now();
    const zonedNow = now.setZone('UTC').plus({ minutes: now.offset });
    const start = zonedNow.startOf('day').toISO();
    const end = zonedNow.toISO();

    return { start, end };
}

/**
 * Get default date-range (30 days for daily usage, unlimited for distribution)
 */
export function getDefaultDateRange(type: 'daily' | 'detailed' | 'distribution'): {
    start: string | null;
    end: string | null;
} {
    if (type === 'daily') {
        return getDateRange(30);
    }
    // For detailed and distribution, return a larger range or let filter component handle it
    return getDateRange(90);
}

/**
 * Re-export day of week utilities
 */
export * from './daysOfWeek';
